/**
 * DashboardContainer
 * --
 */
// import React from 'react';
import React, { useEffect, useState } from 'react';
import { useGetOrders } from 'hooks/OrderHooks';
import { useGetUsers } from 'hooks/UserHooks';
import MessageAlert from 'utils';
import { useUserStack } from 'store';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import DashboardPresenter from './DashboardPresenter';
import * as XLSX from 'xlsx/xlsx.mjs';
import { StatisticsApi, ShopInformationApi } from 'api';

const MOMENT_FORMAT = 'YYYY-MM-DD(dddd)';
const initialToday = moment().format(MOMENT_FORMAT);

/**----------------------------------------
 * [Component] 매출관리 > 대시보드 - 컨테이너
 * ----------------------------------------
 */
const DashboardContainer = () => {
  /* ===== Init ===== */
  // 오늘 날짜의 시작 (00:00:00)과 끝 (23:59:59) 타임스탬프 계산
  const { type } = useParams();
  const userInfo = useUserStack();

  /* ===== VARIABLE ===== */

  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [startOfToday, setStartOfToday] = useState(
    moment(initialToday).add(-28, 'days').startOf('day').unix()
  );
  const [endOfToday, setEndOfToday] = useState(
    moment(initialToday).endOf('day').unix()
  );

  const { data: Orders, refetch } = useGetOrders({
    start_date: startOfToday,
    end_date: endOfToday,
  });
  const { data: Users } = useGetUsers();

  /* 주문데이터 */
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [originData, setOriginData] = useState([]);
  const [weeklyData, setWeeklyData] = useState([]);
  const [filterKey, setFilterKey] = useState('ALL');
  // 오늘날짜
  const [today, setToday] = useState(initialToday);
  const [todayOrderList, setTodyOrderList] = useState([]);
  // 주문목록
  const [orderList, setOrderList] = useState(Orders?.data ? Orders.data : []);
  // const [topTab, setTopTab] = useState('product'); // product, category
  const [classificationTab, setClassificationTab] = useState('daily'); // daily
  const [todayRankType, setTodayRankType] = useState('product_sort');

  /* 메장별데이터 */
  // 메뉴 매출 데이터
  const [menuSales, setMenuSales] = useState([]);
  // 일별 매출 상태
  const [dailySales, setDailySales] = useState({});
  const [dailyRefundSales, setDailyRefundSales] = useState({});
  const [dailyCouponSales, setDailyCouponSales] = useState({});
  const [currentShop, setCurrentShop] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');

  /* ===== FUNCTION ===== */
  /**--------------------
   *
   * --------------------
   */
  const getSortedMenuData = (orders) => {
    // 메뉴 이름별 판매 수량 합산 및 ID, 가격 유지
    const menuSales =
      orders &&
      orders.reduce((acc, order) => {
        order.product_information.forEach((menu) => {
          const { menu_id, menu_name, num, price } = menu;

          if (acc[menu_name]) {
            acc[menu_name].num += num; // 수량 합산
          } else {
            acc[menu_name] = {
              menu_id,
              menu_name,
              num,
              price,
              name: menu_name,
            }; // 처음 추가
          }
        });
        return acc;
      }, {});

    // 객체를 배열로 변환하여 정렬
    const sortedMenuArray =
      menuSales &&
      Object.entries(menuSales)
        .map(([menu_name, { menu_id, num, price, name }]) => ({
          menu_name,
          menu_id,
          num,
          price,
          name,
        }))
        .sort((a, b) => b.num - a.num); // 수량을 기준으로 내림차순 정렬

    return sortedMenuArray;
  };

  /**--------------------
   * 차트) 플랫폼 매출 필터 함수
   * --------------------
   */
  const handleCalculatePlatformData = (orders) => {
    // 플랫폼 이름별 판매 수량 합산 및 ID, 가격 유지
    const platformSales =
      orders &&
      orders.reduce((acc, order) => {
        const { order_route, final_price, payment_state } = order;

        if (payment_state === 'COMPLETED') {
          if (acc[order_route]) {
            acc[order_route].total += final_price;
          } else {
            acc[order_route] = { name: order_route, total: final_price };
          }
        }
        return acc;
      }, {});

    // 객체를 배열로 변환하여 정렬
    const sortedPlatformArray =
      platformSales &&
      Object.entries(platformSales)
        .map(([key, value]) => ({
          ...value,
        }))
        .sort((a, b) => b.total - a.total); // 수량을 기준으로 내림차순 정렬

    return sortedPlatformArray;
  };

  /**--------------------
   * 다음날/이전날 이동 함수
   * --------------------
   */
  const handleChangeDay = (type = 'prev') => {
    // 로딩처리
    setIsLoading(true);

    let calcDay;

    let todayDate = today;
    // 오늘로 돌아가기처리
    if (type === 'today') {
      todayDate = initialToday;
      calcDay = 0;
    }

    // 어제/오늘 구분
    else if (type === 'prev') {
      calcDay = -1;
    } else if (type === 'next') {
      calcDay = 1;
    }

    // 날짜계산
    const changeDate = moment(todayDate)
      .add(calcDay, 'days')
      .format(MOMENT_FORMAT);
    const std = moment(changeDate).add(-28, 'days').startOf('day').unix();
    const edd = moment(changeDate).endOf('day').unix();

    // console.log('std: ', std);
    // console.log('edd: ', edd);

    // 날짜변경
    setToday(changeDate);
    // setStartOfToday(moment(today).startOf('day').unix());
    setStartOfToday(std);
    setEndOfToday(edd);

    // 로딩처리
    setTimeout(() => {
      setIsLoading(false);
    }, 400);
  };

  /**--------------------
   *
   * --------------------
   */
  const call1 = (startDate, endDate) => {
    // Users 데이터에서 user_id를 기준으로 사용자 정보를 추가
    const userMap = Users?.data?.reduce((acc, user) => {
      acc[user.user_id] = user;
      return acc;
    }, {});

    const originOrders = Orders?.data
      ?.filter(
        (item) =>
          item?.shop_information_id === userInfo?.shop_information_id &&
          item?.payment_state &&
          ['COMPLETED', 'REFUNDED'].includes(item.payment_state)
      )
      ?.map((order) => ({
        ...order,
        user: userMap?.[order?.user_id] || {},
        order_status: order?.order_status,
        menu: order?.product_information?.map((menuItem) => ({
          menu_name: menuItem.menu_name,
          num: menuItem.num,
          price: menuItem.price,
          option: menuItem.menu_option?.map((option) => ({
            option_id: option.option_id,
            option_title: option.option_title,
            option_type: option.option_type,
            option: option.option?.map((opt) => ({
              option_key: opt.option_key,
              option_name: opt.option_name,
              option_type: opt.option_type,
              option_price: opt.option_price,
              count: opt.count,
              default: opt.default,
            })),
          })),
        })),
        payment_date: new Date(order?.payment_completion_date * 1000),
        discount: order?.final_discount,
        totalPrice: order?.final_price,
        type: order?.type,
      }));

    const newData = Orders?.data
      ?.filter(
        (item) =>
          item?.shop_information_id === userInfo?.shop_information_id &&
          item?.payment_state &&
          ['COMPLETED', 'REFUNDED'].includes(item.payment_state) &&
          // NOTE: 당일 주문만 표시
          item?.payment_completion_date >= startDate &&
          item?.payment_completion_date <= endDate
      )
      ?.map((order) => ({
        ...order,
        user: userMap?.[order?.user_id] || {},
        order_status: order?.order_status,
        menu: order?.product_information?.map((menuItem) => ({
          menu_name: menuItem.menu_name,
          num: menuItem.num,
          price: menuItem.price,
          option: menuItem.menu_option?.map((option) => ({
            option_id: option.option_id,
            option_title: option.option_title,
            option_type: option.option_type,
            option: option.option?.map((opt) => ({
              option_key: opt.option_key,
              option_name: opt.option_name,
              option_type: opt.option_type,
              option_price: opt.option_price,
              count: opt.count,
              default: opt.default,
            })),
          })),
        })),
        payment_date: new Date(order?.payment_completion_date * 1000),
        discount: order?.final_discount,
        totalPrice: order?.final_price,
        type: order?.type,
      }));

    // filterKey에 따라 상위 탭 필터링
    const filteredOrders =
      filterKey === 'ALL'
        ? newData
        : filterKey === 'REFUNDED'
        ? originData?.filter((item) => item?.payment_state === filterKey)
        : filterKey === 'ALLORDER'
        ? originData
        : newData?.filter((item) => item?.order_route === filterKey);

    // 최신순 정렬 (payment_date 기준 내림차순 정렬)
    const sortedOrders = filteredOrders?.sort(
      (a, b) => b?.payment_date - a?.payment_date
    );

    const st = moment(today).startOf('day').unix(); // 오늘 시작
    const ed = moment(today).endOf('day').unix(); // 오늘 종료

    // 오늘날짜 필터
    const todayDataRaw = originOrders?.filter(
      (item) => item.created_at >= st && item.created_at <= ed
    );

    const weeklyDataRaw = handleCalculateDailyData(originOrders);

    // 일별 주문내역 데이터
    setTodyOrderList(todayDataRaw);

    setData(sortedOrders);
    // 오늘 데이터
    setOriginData(todayDataRaw);
    // 주간
    setWeeklyData(weeklyDataRaw);
    // eslint-disable-next-line
  };

  /**--------------------
   * 차트) 주간 매출 필터 함수
   * --------------------
   */
  const handleCalculateWeeklyData = (d) => {
    // 주간날짜 필터
    const today22 = moment();
    // 4주간 주차별 데이터를 분류
    const weeklyDataRaw = d
      ?.reduce((acc, item) => {
        const createdAtMoment = moment.unix(item.created_at);
        const weeksAgo = Math.floor(today22.diff(createdAtMoment, 'days') / 7); // 오늘 기준 몇 주 전인지 계산

        if (weeksAgo < 4) {
          // 4주 이내의 데이터만 처리
          if (!acc[weeksAgo]) {
            acc[weeksAgo] = []; // 해당 주차가 없으면 배열 생성
          }
          acc[weeksAgo].push(item); // 주차별로 데이터 추가
        }
        return acc;
      }, [])
      .map((item, idx) => {
        // 금액 합산해서 계산하는 리듀스
        return {
          name: `최근 ${idx + 1}주`,
          ...item.reduce(
            (acc, cur) => {
              return {
                ...acc,
                final_price: acc.final_price + cur.final_price,
                final_discount:
                  acc.final_discount + cur.final_price + cur.final_discount,
              };
            },
            {
              final_price: 0,
              final_discount: 0,
            }
          ),
        };
      });
    return weeklyDataRaw;
  };

  /**--------------------
   * 차트) 일간 매출 필터 함수
   * --------------------
   */
  const handleCalculateDailyData = (d) => {
    // 주간날짜 필터
    const today22 = moment();
    // 4주간 주차별 데이터를 분류
    const result = d
      ?.reduce((acc, item) => {
        const createdAtMoment = moment.unix(item.created_at);
        const daysAgo = today22.diff(createdAtMoment, 'days'); // 오늘 기준 며칠 전인지 계산

        if (daysAgo < 10) {
          // 4주 이내의 데이터만 처리
          if (!acc[daysAgo]) {
            acc[daysAgo] = []; // 해당 주차가 없으면 배열 생성
          }
          acc[daysAgo].push(item); // 주차별로 데이터 추가
        }
        return acc;
      }, [])
      .map((item, idx) => {
        // 금액 합산해서 계산하는 리듀스
        return {
          idx: idx + 1,
          name: `${idx + 1}일 전`,
          ...item.reduce(
            (acc, cur) => {
              return {
                ...acc,
                final_price: acc.final_price + cur.final_price,
                final_discount:
                  acc.final_discount + cur.final_price + cur.final_discount,
              };
            },
            {
              final_price: 0,
              final_discount: 0,
            }
          ),
        };
      })
      .sort((a, b) => (a.day < b.day ? 1 : a.day > b.day ? -1 : 0))
      .filter((a) => a !== undefined && a !== null);

    return result;
  };

  /**--------------------
   * 차트) 결제수단별 매출 필터 함수
   * --------------------
   */
  const handleCalculatePaymethodData = (d) => {
    const result = d?.reduce((acc, item) => {
      const paymentMethod = item.payment_method;

      if (!acc[paymentMethod]) {
        acc[paymentMethod] = []; // 해당 payment_method가 없으면 배열 생성
      }

      acc[paymentMethod].push(item); // payment_method별로 데이터 추가

      return acc;
    }, {});
    const result2 = Object.keys(result)
      .map((item, idx) => {
        // 금액 합산해서 계산하는 리듀스
        return {
          idx: idx + 1,
          name: item,
          ...result[item].reduce(
            (acc, cur) => {
              return {
                ...acc,
                final_price: acc.final_price + cur.final_price,
                final_discount:
                  acc.final_discount + cur.final_price + cur.final_discount,
              };
            },
            {
              final_price: 0,
              final_discount: 0,
            }
          ),
        };
      })
      .sort((a, b) =>
        a.final_price < b.final_price
          ? 1
          : a.final_price > b.final_price
          ? -1
          : 0
      );

    return result2;
  };

  /**--------------------
   * 엑셀 다운로드
   * --------------------
   */
  const handleDownloadExel = () => {
    // console.log('data: ', data);
    // return;
    setIsLoading(true);

    try {
      const xlsxData = orderList.map((item, idx) => {
        const menuLength = item.product_information.length;
        const ml = menuLength > 1 ? `외 ${menuLength - 1}건` : ``;

        return {
          ['순번']: idx + 1,
          ['주문내역']: `${item?.product_information[0].menu_name} ${ml}`,
          ['결제수단']: `${item.payment_method}`,
          ['결제시간']: moment(item.payment_date).format('YYYY-MM-DD HH:mm:ss'),
          ['결제금액']: item.final_price,
          ['결제시리얼No']: item.payment_serial_number,
          ['주문번호']: item.order_number,
          ['주문자정보']: item.user
            ? `${item.user.user_name}(${item.user.user_id})`
            : ``,
        };
      });

      const ws = XLSX.utils.json_to_sheet(xlsxData);
      const wb = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      XLSX.writeFile(wb, `주문내역_${moment().format('YYYYMMDDHHmmss')}.xlsx`);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log(e);
      MessageAlert.error(e.message);
    }
  };

  /* ===== HOOKS ===== */
  /**--------------------
   * 데이터 초기화
   * --------------------
   */
  useEffect(() => {
    call1(startOfToday, endOfToday);
  }, [startOfToday, Orders, Users, filterKey, today]);

  /**--------------------
   * 데이터 초기화
   * --------------------
   */
  // useEffect(() => {
  //   const st = moment(today).startOf('day').unix();
  //   const ed = moment(today).endOf('day').unix();

  //   call1(st, ed);
  // }, [today]);

  /**--------------------
   * 오더 데이터 정제
   * --------------------
   */
  useEffect(() => {
    const orders = Orders?.data?.filter(
      (item) => item?.shop_information_id === userInfo?.shop_information_id
    );
    // const menuData = getSortedMenuData(orders);

    if (orders) {
      const { salesData, refundedData, couponDiscountData } = orders?.reduce(
        (acc, order) => {
          const date = moment.unix(order.created_at).format('YYYY-MM-DD');

          if (order?.payment_state === 'COMPLETED') {
            acc.salesData[date] =
              (acc.salesData[date] || 0) + order.final_price;
          }

          if (order?.payment_state === 'REFUNDED') {
            acc.refundedData[date] =
              (acc.refundedData[date] || 0) + order.final_price;
          }

          if (order?.payment_state === 'COMPLETED') {
            if (order?.coupon_info && Array.isArray(order.coupon_info)) {
              const totalDiscount = order.coupon_info.reduce(
                (sum, coupon) => sum + (coupon.discount_price || 0),
                0
              );
              acc.couponDiscountData[date] =
                (acc.couponDiscountData[date] || 0) + totalDiscount;
            }
          }

          return acc;
        },
        { salesData: {}, refundedData: {}, couponDiscountData: {} } // 초기값 설정
      );
      setDailySales(salesData);
      setDailyRefundSales(refundedData);
      setDailyCouponSales(couponDiscountData);
    }

    // console.log('menuData: ', menuData);
    // setMenuSales(menuData);
  }, [Orders]);

  /**--------------------
   *
   * --------------------
   */
  useEffect(() => {
    let result;

    // 일별 매출
    if (classificationTab === 'daily') {
      result = handleCalculateDailyData(Orders?.data);
    }
    // 주별 매출
    else if (classificationTab === 'weekly') {
      result = handleCalculateWeeklyData(Orders?.data);
    }
    // 결제수단 매출
    else if (classificationTab === 'paymethod') {
      result = handleCalculatePaymethodData(Orders?.data);
    } else {
      result = [];
    }

    setWeeklyData(result);
  }, [classificationTab]);

  /**--------------------
   *
   * --------------------
   */
  useEffect(() => {
    let result;

    // 상품별
    if (todayRankType === 'product_sort') {
      result = getSortedMenuData(todayOrderList);
    }
    // 카테고리별
    else if (todayRankType === 'platform_sort') {
      result = handleCalculatePlatformData(todayOrderList);
    }

    setMenuSales(result);
  }, [todayRankType, todayOrderList]);

  /**
   *
   *
   */
  useEffect(() => {
    const call = async () => {
      try {
      } catch (e) {
        MessageAlert.error(e.message);
      }
    };
    currentShop && call();
  }, [currentShop]);

  /* ===== RENDER ===== */
  /*  */
  const totalPrice = originData
    ? originData.reduce((acc, cur) => {
        if (cur?.payment_state === 'COMPLETED') {
          acc += cur?.totalPrice;
        }
        return acc;
      }, 0)
    : 0;
  /*  */
  const discountPrice = originData
    ? originData?.reduce((acc, cur) => {
        // if (
        //   cur?.payment_state === 'COMPLETED' &&
        //   Array.isArray(cur?.coupon_info)
        // ) {
        //   acc += cur?.coupon_info.reduce((couponAcc, coupon) => {
        //     return couponAcc + (coupon?.discount_price || 0);
        //   }, 0);
        // }
        if (cur?.payment_state === 'COMPLETED') {
          acc += cur?.discount;
        }
        return acc;
      }, 0)
    : 0;
  /*  */
  const refundPrice = originData
    ? originData?.reduce((acc, cur) => {
        if (cur?.payment_state === 'REFUNDED') {
          acc += cur?.totalPrice;
        }
        return acc;
      }, 0)
    : 0;
  /*  */
  const orderUsers = todayOrderList?.reduce(
    (acc, cur) => {
      const userId = cur?.user_id;

      // 비회원일 경우 카운트
      if (!userId) {
        acc.total += 1;
      }
      // 중복 회원인지 체크
      else if (!acc.userIds.has(userId)) {
        acc.userIds.add(userId); // 중복 방지
        acc.users.push(cur.user); // 유저 추가
        acc.total += 1;
      }

      return acc;
    },
    {
      userIds: new Set(),
      users: [], // 회원
      total: 0, // 총 주문 회원(회원 + 비회원)
    }
  );

  /* VIEW */
  return (
    <>
      <DashboardPresenter
        today={today}
        currentShop={currentShop}
        setCurrentShop={setCurrentShop}
        classificationTab={classificationTab}
        setClassificationTab={setClassificationTab}
        isLoading={isLoading}
        userList={orderUsers}
        todayOrderList={todayOrderList}
        orderList={originData}
        orderListRaw={Orders && Orders.data ? Orders.data : []}
        totalPrice={totalPrice}
        discountPrice={discountPrice}
        refundPrice={refundPrice}
        chartData={{
          menuSales,
          weeklyData,
        }}
        onChangeDay={handleChangeDay}
        onDownloadExel={handleDownloadExel}
        todayRankType={todayRankType}
        setTodayRankType={setTodayRankType}
        searchKeyword={searchKeyword}
        setSearchKeyword={setSearchKeyword}
      />
    </>
  );
};

export default DashboardContainer;
